/**
 * Transfer of the seller's valuation inputs (/evaluare-auto or /vinde-masina)
 * to the private CreateListingV2 form. The AI video prefill always wins; the
 * valuation only fills fields that are still empty. The price is never filled,
 * only offered as a hint. The storage key is removed as soon as it is read.
 */
export const SELL_PREFILL_STORAGE_KEY = 'ram_sell_valuation_prefill';

export interface SellValuationSnapshot {
  make?: string;
  model?: string;
  year?: string;
  mileage?: string;
  fuel?: string; // valuation codes: benzina, diesel, hybrid, hybrid_plugin, electric, lpg
  transmission?: string; // manuala, automata
  has_estimate: boolean;
  recommended_price?: number;
  estimated_low?: number;
  estimated_high?: number;
}

export interface PriceHint { recommended: number; low?: number; high?: number }

const FUEL_MAP: Record<string, string> = {
  benzina: 'petrol', diesel: 'diesel', hybrid: 'hybrid', hybrid_plugin: 'plug-in-hybrid', electric: 'electric', lpg: 'lpg',
};
const TRANSMISSION_MAP: Record<string, string> = { manuala: 'manual', automata: 'automatic' };

export function saveSellPrefill(s: SellValuationSnapshot): void {
  try {
    if (typeof window === 'undefined') return;
    if (!s.make && !s.model && !s.year) return;
    window.sessionStorage.setItem(SELL_PREFILL_STORAGE_KEY, JSON.stringify(s));
  } catch { /* storage unavailable */ }
}

// The form can mount twice on one page load (dev StrictMode, guards). The value read from
// storage is kept in memory for a few seconds so the second mount gets it too.
let recent: { at: number; value: SellValuationSnapshot } | null = null;

export function consumeSellPrefill(): SellValuationSnapshot | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(SELL_PREFILL_STORAGE_KEY);
    if (!raw) return recent && Date.now() - recent.at < 5000 ? recent.value : null;
    window.sessionStorage.removeItem(SELL_PREFILL_STORAGE_KEY);
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    recent = { at: Date.now(), value: v as SellValuationSnapshot };
    return recent.value;
  } catch {
    return null;
  }
}

export function _resetSellPrefillMemory() { recent = null; }

/** Reads ?make=&model=&year=&mileage=&fuel=&transmission= as links from /evaluare-auto build them. */
export function snapshotFromSearch(search: string): Partial<SellValuationSnapshot> | null {
  const p = new URLSearchParams(search);
  const clean = (k: string, max = 60) => {
    const v = p.get(k)?.trim();
    return v ? v.slice(0, max) : undefined;
  };
  const year = clean('year', 4);
  const mileage = clean('mileage', 7);
  const out: Partial<SellValuationSnapshot> = {
    make: clean('make'),
    model: clean('model', 80),
    year: year && /^\d{4}$/.test(year) ? year : undefined,
    mileage: mileage && /^\d+$/.test(mileage) ? mileage : undefined,
    fuel: clean('fuel', 20),
    transmission: clean('transmission', 20),
  };
  return Object.values(out).some(Boolean) ? out : null;
}

type FormLike = {
  category_code: string;
  make: string;
  model: string;
  year: number;
  mileage_value: string;
  fuel: string;
  transmission: string;
};

/**
 * Applies the valuation only to empty fields. `defaultYear` is the form's initial
 * year: it counts as empty because the seller has not chosen it.
 */
export function applySellPrefill<T extends FormLike>(form: T, s: SellValuationSnapshot, defaultYear: number): { next: T; filled: number } {
  const next = { ...form };
  let filled = 0;
  const empty = (v: unknown) => v === undefined || v === null || v === '';
  if (empty(next.make) && s.make) { next.make = s.make; filled++; if (empty(next.model) && s.model) { next.model = s.model; filled++; } }
  else if (empty(next.model) && s.model && next.make === s.make) { next.model = s.model; filled++; }
  const y = Number(s.year);
  if (next.year === defaultYear && Number.isInteger(y) && y >= 1950 && y <= defaultYear + 1 && y !== defaultYear) { next.year = y; filled++; }
  if (empty(next.mileage_value) && s.mileage && /^\d+$/.test(s.mileage)) { next.mileage_value = s.mileage; filled++; }
  const fuel = s.fuel ? FUEL_MAP[s.fuel] : undefined;
  if (empty(next.fuel) && fuel) { next.fuel = fuel; filled++; }
  const tr = s.transmission ? TRANSMISSION_MAP[s.transmission] : undefined;
  if (empty(next.transmission) && tr) { next.transmission = tr; filled++; }
  if (filled > 0 && empty(next.category_code)) next.category_code = 'cars';
  return { next, filled };
}

export function priceHintFrom(s: SellValuationSnapshot | null): PriceHint | null {
  if (!s?.has_estimate || !s.recommended_price || s.recommended_price <= 0) return null;
  return { recommended: Math.round(s.recommended_price), low: s.estimated_low, high: s.estimated_high };
}
